import {
	DimensionRecommendationKind,
	FormItem,
	TextInput,
	ValidationError
} from "client/Components";
import { IObjectInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { FormModel } from "./EditCommunityDialog";
import { ReactElement, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { ImageSelect } from "../../ImageSelect/ImageSelect";
import {
	CommunityCoverImageSaveModel,
	CommunityCoverImageViewModel,
	CommunityImageRestrictions
} from "client/Api/Communities/community";
import { getCommunityImageUrl } from "../common";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";
import styles from "./Settings.module.less";

export type SettingsProps = {
	attachable: IValueInputAttachable<FormModel>;
	areNameAndDescriptionEditable: boolean;
	originalImage: CommunityCoverImageViewModel | null;
	imageRestrictions: CommunityImageRestrictions;
	onFileChange: (file: Base64FileUriUploadWithMetadataModel | null) => void;
	onEditFocalPoint: () => void;
};

export const Settings = (props: SettingsProps): ReactElement => {
	const {
		attachable,
		areNameAndDescriptionEditable,
		originalImage,
		imageRestrictions,
		onFileChange,
		onEditFocalPoint
	} = props;

	const {
		maxFileSize,
		supportedImageTypes
	} = imageRestrictions;

	const imageAttachable: IObjectInputAttachable<CommunityCoverImageSaveModel | null> = attachable.for("image");
	const image: CommunityCoverImageSaveModel | null = imageAttachable.value;

	const imageSrc = useMemo(
		(): string | null => {
			if (image === null) {
				return null;
			}

			const { file } = image;

			if (file.isSet) {
				return file.value
					.uri;
			}

			if (originalImage) {
				return getCommunityImageUrl(originalImage.file.blobId);
			}

			return null;
		},
		[image, originalImage]
	);

	const isFileValid: boolean | undefined = imageAttachable
		.whenSet(
			(x) => {
				return x.for("file")
					.validation
					.isValid();
			},
			() => undefined
		);

	return (
		<div className={styles.root}>
			<FormItem
				label={localize("_Name")}
				isRequired
			>
				<TextInput
					{...attachable.for("name")
						.attachToTextInput()
					}
					isDisabled={!areNameAndDescriptionEditable}
				/>

				<ValidationError
					{...attachable.for("name")
						.attachToValidationError()
					}
				/>
			</FormItem>

			<FormItem
				label={localize("_Description")}
			>
				<TextInput
					{...attachable.for("description")
						.attachToTextInput()
					}
					isDisabled={!areNameAndDescriptionEditable}
				/>

				<ValidationError
					{...attachable.for("description")
						.attachToValidationError()
					}
				/>
			</FormItem>

			<FormItem label={localize("_Image")}>
				<ImageSelect
					trackingId="select-image"
					src={imageSrc}
					onChange={onFileChange}
					onEditFocalPoint={
						isFileValid === true
							? onEditFocalPoint
							: undefined
					}
					maxImageSize={maxFileSize}
					supportedFormats={supportedImageTypes}
					hasError={!imageAttachable.validation.isValid()}
					dimensionRecommendation={{
						kind: DimensionRecommendationKind.DimensionWithAspectRatio,
						height: 300,
						width: 600,
						aspectRatio: "2:1"
					}}
					supportedFormatsText={[
						localize("_FileExtensionJpg"),
						localize("_FileExtensionPng"),
						localize("_FileExtensionGif")
					]}
				/>

				<ValidationError {...attachable.for("image").attachToValidationError(true)} />

				{attachable.for("image")
					.whenSet(
						(x) => (
							<>
								<ValidationError {...x.for("file").attachToValidationError()} />
								<ValidationError {...x.for("focalPoint").attachToValidationError()} />
							</>
						)
					)
				}
			</FormItem>
		</div>
	);
};
Settings.displayName = "Settings";
